import React from 'react';

import { useDynamicIcon } from '@lumapps/assets/hooks/useDynamicIcon';
import { enabledFeaturesSelector } from '@lumapps/features';
import { Avatar, Badge, ColorPalette, Icon } from '@lumapps/lumx/react';
import { useSelector } from '@lumapps/redux/react';
import { UserAvatar } from '@lumapps/user-ui/components/UserAvatar';
import { User } from '@lumapps/user/types';

import { getNotificationMeta } from '../../notification-meta';
import { Notification } from '../../types';
import { getModernNotificationIconPath } from '../../utils/iconProps/modernIconMap';
import { isModernNotification } from '../../utils/isModernNotification';

export type NotificationListItemBeforeProps = {
    className?: string;
    // The notification
    notification: Notification;
    // Details about the sender
    senderDetails?: User | null;
};

export const NotificationListItemBefore: React.FC<NotificationListItemBeforeProps> = ({
    className,
    notification,
    senderDetails,
}) => {
    const enabledFeatures = useSelector(enabledFeaturesSelector);

    // Hooks must run unconditionally on every render, so the legacy meta lookup and the
    // `useDynamicIcon` call below happen regardless of shape — only the JSX returned differs.
    const {
        hasThumbnail = false,
        iconProps = {},
        getIconProps,
        hasBadge = hasThumbnail,
    } = isModernNotification(notification) ? {} : getNotificationMeta(notification.type);
    const legacyIconProps = getIconProps ? getIconProps({ enabledFeatures, iconProps }) : iconProps;
    const { iconPath: iconDynamicPath } = useDynamicIcon({ icon: legacyIconProps?.icon });

    if (isModernNotification(notification)) {
        const iconPath = getModernNotificationIconPath(notification.icon);
        const badge = (
            <Badge color={ColorPalette.primary}>
                <Icon icon={iconPath} />
            </Badge>
        );

        if (notification.thumbnail?.type === 'user_avatar') {
            return <UserAvatar user={notification.thumbnail.userPreview} badge={badge} />;
        }

        if (notification.thumbnail?.type === 'absolute_url') {
            // eslint-disable-next-line deprecation/deprecation
            return <Avatar className={className} image={notification.thumbnail.value} alt="" badge={badge} />;
        }

        return <Icon className={className} icon={iconPath} hasShape />;
    }

    const iconPath = legacyIconProps?.customIcon || iconDynamicPath;

    if (hasThumbnail && senderDetails) {
        return (
            <UserAvatar
                user={senderDetails}
                useAvatarFromApiProfile
                badge={
                    hasBadge ? (
                        <Badge color={legacyIconProps?.color}>
                            <Icon icon={iconPath} />
                        </Badge>
                    ) : undefined
                }
            />
        );
    }

    return (
        <Icon
            className={className}
            icon={iconPath}
            color={legacyIconProps?.color}
            colorVariant={legacyIconProps?.colorVariant}
            hasShape
        />
    );
};
